import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { IEntity } from "@/lib/utility/data/entity.interface";
import inboxService from "@/services/mrfiktiv/services/inboxService";
import {
  MrfiktivInboxToolDtoGen,
  MrfiktivInboxViewModelGen,
  MrfiktivUpdateInboxDtoGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ITimestamp, Timestamp } from "./timestamp.entity";
import { InboxDataAccessLayer } from "@/store/modules/access-layers/inbox.access-layer";

@IsFilterable
class InboxBase implements IEntity<IInbox, MrfiktivUpdateInboxDtoGen>, MrfiktivInboxViewModelGen {
  loading = false;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.inbox.id"
  })
  id: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.inbox.partnerId"
  })
  partnerId: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.source"
  })
  source: "email" | "sms" | "whatsapp";

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.name"
  })
  name: string;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.identifier"
  })
  identifier: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.inbox.isActive"
  })
  isActive: boolean;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.inbox.isPublic"
  })
  isPublic: boolean;

  @FilterConfig({
    type: FilterTypes.STRING,
    displayName: "objects.inbox.notificationRecipients"
  })
  notificationRecipients: string[];

  tools: MrfiktivInboxToolDtoGen[];

  @FilterConfig({
    type: Timestamp
  })
  timestamp: ITimestamp;

  constructor(data?: Partial<MrfiktivInboxViewModelGen>) {
    this.id = data?.id || "";
    this.partnerId = data?.partnerId || "";
    this.source = data?.source || "email";
    this.name = data?.name || "";
    this.identifier = data?.identifier || "";
    this.isActive = data?.isActive ?? false;
    this.isPublic = data?.isPublic ?? true;
    this.notificationRecipients = data?.notificationRecipients ? [...data.notificationRecipients] : [];
    this.tools = data?.tools ? [...data.tools] : [];
    this.timestamp = new Timestamp(data?.timestamp);
  }

  map(data: MrfiktivInboxViewModelGen): void {
    this.id = data.id || "";
    this.partnerId = data.partnerId || "";
    this.source = data.source || "email";
    this.name = data.name || "";
    this.identifier = data.identifier || "";
    this.isActive = data.isActive ?? false;
    this.isPublic = data.isPublic ?? true;
    this.notificationRecipients = data.notificationRecipients ? [...data.notificationRecipients] : [];
    this.tools = data.tools ? [...data.tools] : [];
    this.timestamp = new Timestamp(data.timestamp);
  }

  async create(): Promise<this> {
    const res = await inboxService.create(this.partnerId, {
      name: this.name,
      identifier: this.identifier || "",
      isActive: this.isActive,
      isPublic: this.isPublic,
      notificationRecipients: this.notificationRecipients
    });

    this.map(res);
    InboxDataAccessLayer.set(this);

    return this;
  }

  async fetch(): Promise<this> {
    this.loading = true;
    try {
      const res = await inboxService.getOne(this.partnerId, this.id);
      this.map(res);
      InboxDataAccessLayer.set(this);
    } catch (e) {
      this.loading = false;
      InboxDataAccessLayer.delete(this);
      throw e;
    }
    this.loading = false;

    return this;
  }

  async updatePartial(dto: Partial<MrfiktivUpdateInboxDtoGen>): Promise<this> {
    const res = await inboxService.update(this.partnerId, this.id, dto);

    this.map(res);
    InboxDataAccessLayer.set(this);

    return this;
  }

  async update(): Promise<this> {
    this.loading = true;
    try {
      await this.updatePartial({
        source: this.source,
        name: this.name,
        identifier: this.identifier,
        isActive: this.isActive,
        isPublic: this.isPublic,
        notificationRecipients: this.notificationRecipients,
        tools: this.tools
      });
    } finally {
      this.loading = false;
    }

    return this;
  }

  upsertDocumentRuleCheckTool(isActive: boolean, ruleSetId: string | null): void {
    const others = this.tools.filter((t) => t.type !== "documentRuleCheck");
    this.tools = [
      ...others,
      {
        type: "documentRuleCheck",
        isActive,
        documentRuleCheckConfig: { ruleSetId }
      }
    ];
  }

  async delete(): Promise<void> {
    await inboxService.delete(this.partnerId, this.id);

    InboxDataAccessLayer.delete(this);
  }
}

type IInbox = InboxBase;
const Inbox = Filter.createForClass(InboxBase);

export { IInbox, Inbox };
